import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import { getLeagueAdmin } from "core-integration/src/store/leagues/reducers";
import {
  fetchH2HLeagueForAdmin,
  updateH2HLeague,
} from "core-integration/src/store/leagues/thunks";
import {
  IPrivateLeagueAdmin,
  IUpdateH2HLeagueData,
} from "core-integration/src/store/leagues/types";
import HorizontalDivider from "plos/src/components/HorizontalDivider";
import ButtonLink from "plos/src/components/links/ButtonLink";
import PageTitle from "plos/src/components/PageTitle";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import { rightSidebarLayout } from "plos/src/layouts";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useTrackingContext } from "../../../../contexts/TrackingContext";
import { gridDivider } from "../../../LeaguesAndCups/leaguesAndCups.css";
import HelmetHeadLeagues from "../../HelmetHeadLeagues";
import { AddBan } from "../AddBan";
import { ChangeAdmin } from "../ChangeAdmin";
import { Code } from "../Code";
import { Delete } from "../Delete";
import H2HDetails from "../H2HDetails";
import { RemoveBan } from "../RemoveBan";
import {
  actionButtons,
  adminContentGrid,
  adminH2HWrapper,
  gridItem,
} from "./adminH2H.css";

const AdminH2H = () => {
  const { t } = useTranslation("leaguesAndCups");
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useLocalizedNavigate();
  const { leagueId } = useParams();

  const { firePageViewEvent } = useTrackingContext();

  const leagueNumber = leagueId ? parseInt(leagueId, 10) : 0;
  const league = useSelector((state: RootState) =>
    leagueNumber
      ? (getLeagueAdmin(state, leagueNumber) as IPrivateLeagueAdmin)
      : null,
  );
  const now = useSelector(getCurrentEvent);

  useEffect(() => {
    firePageViewEvent("fantasy leagues & cups", "admin head to head");
  }, []);

  useEffect(() => {
    if (leagueNumber) {
      dispatch(fetchH2HLeagueForAdmin(leagueNumber));
    }
  }, [dispatch, leagueNumber]);

  if (!league) {
    return null;
  }

  const updateLeague = async (leagueId: number, data: IUpdateH2HLeagueData) => {
    await dispatch(updateH2HLeague(leagueId, data));
    navigate("/leagues");
  };

  const started = Boolean(now && now.id >= league.start_event);

  return (
    <>
      <HelmetHeadLeagues />
      <div className={rightSidebarLayout}>
        <SurfaceContainer>
          <div className={adminH2HWrapper}>
            <div className={actionButtons}>
              <PageTitle
                title={t("leagueAdministration", "League Administration")}
              />
              <ButtonLink size="small" to={`/leagues/${leagueNumber}/invite`}>
                {t("invitePlayers", "Invite players")}
              </ButtonLink>
            </div>
            {!started && (
              <>
                <Code league={league} />
                <div className={gridDivider}>
                  <HorizontalDivider />
                </div>
              </>
            )}

            <H2HDetails league={league} started={started} />
            <div className={adminContentGrid}>
              {!started && (
                <>
                  <div className={gridDivider}>
                    <HorizontalDivider />
                  </div>
                  <div className={gridItem}>
                    <AddBan league={league} />
                  </div>
                </>
              )}

              {!started && league.bans.length ? (
                <>
                  <div className={gridDivider}>
                    <HorizontalDivider />
                  </div>
                  <div className={gridItem}>
                    <RemoveBan league={league} />
                  </div>
                </>
              ) : null}

              {!started && (
                <>
                  <div className={gridDivider}>
                    <HorizontalDivider />
                  </div>
                  <div className={gridItem}>
                    <ChangeAdmin
                      league={league}
                      updateAdmin={(entryId: number) =>
                        updateLeague(league.id, {
                          admin_entry: entryId,
                          closed: league.closed,
                          code_privacy: league.code_privacy,
                          ko_rounds: league.ko_rounds,
                          max_entries: league.max_entries,
                          name: league.name,
                          start_event: league.start_event,
                        })
                      }
                    />
                  </div>
                </>
              )}
              {!started && (
                <>
                  <div className={gridDivider}>
                    <HorizontalDivider />
                  </div>
                  <div className={gridItem}>
                    <Delete league={league} />
                  </div>
                </>
              )}
            </div>
          </div>
        </SurfaceContainer>
      </div>
    </>
  );
};

export default AdminH2H;
